import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from "react-router-dom";

// Third-party packages.
import TableCSVExport from '../../../component/TableCSVExport';
import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Constants.
import { DATA_EMPTY, DATA_FAILED, DATA_LOADING } from "../../../component/Constants";

// Components.
import AlertRules from "../../../types/Alerts/AlertRules";
import ReactTable from "../../../component/ReactTable";
import { getDefaultPageSize, stringFormatting } from "../../../helpers/utils";
import ConditionalRender from "../../../helpers/ConditionalRender";
import SearchInput from '../../../component/SearchInput';

function List(props: { loading: number, refresh: Function, alertRules: AlertRules[], isAdmin: boolean }) {
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    function refresh() {
        props.refresh();
    }

    function clearTableFilter() {
        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const data: AlertRules[] = useMemo(() => props.alertRules.filter(alertRule => {
        return (
            (alertRule.name === null ? '' : alertRule.name).toLowerCase().includes(tableFilter.toLowerCase()) ||
            (alertRule.entitytype === null ? '' : alertRule.entitytype).toLowerCase().includes(tableFilter.toLowerCase()) ||
            (alertRule.statistic === null ? '' : alertRule.statistic).toLowerCase().includes(tableFilter.toLowerCase()) ||
            (alertRule.entity === null ? '' : alertRule.entity).toLowerCase().includes(tableFilter.toLowerCase()) ||
            (alertRule.metrictype === null ? '' : alertRule.metrictype).toLowerCase().includes(tableFilter.toLowerCase())
        )
    }), [props.alertRules, tableFilter]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            defaultCanSort: false,
        },
        {
            Header: 'Rule',
            accessor: 'name',
            className: 'fw-bold',
            defaultCanSort: true,
            sortType: 'string'
        },
        {
            Header: 'Scope',
            accessor: 'entity',
            defaultCanSort: true,
            Cell: ({ cell }) => (
                <React.Fragment>
                    <span>{`${!cell.row.values.entity ? cell.row.values.entitytype === 'host' ? 'All Hosts' : 'All Instances' : cell.row.values.entity}`}</span>
                </React.Fragment>
            )
        },
        {
            Header: 'Type',
            accessor: 'entitytype',
            defaultCanSort: true,
            Cell: ({ cell }) => (
                <React.Fragment>
                    {/*@ts-ignore*/}
                    <span className='text-capitalize'>{`${cell.row.original.entitytype === 'datasource' ? 'instance' : cell.row.original.entitytype} ${cell.row.original.metrictype}`}</span>
                </React.Fragment>
            )
        },
        {
            Header: 'Metric',
            accessor: 'statistic',
            defaultCanSort: true,
            className: 'text-capitalize',
            Cell: ({ cell }: any) => (
                <React.Fragment>
                    {stringFormatting(cell.row.values.statistic)}
                </React.Fragment>
            )
        },
        {
            Header: 'Threshold',
            accessor: 'threshold',
            defaultCanSort: true,
            Cell: ({ cell }) => (
                <React.Fragment>
                    {/*@ts-ignore*/}
                    {cell.row.original.decreasing ? '<=' : '>='} {cell.row.values.threshold}{cell.row.original.units === '%' ? '%' : cell.row.original.units === 'ms' ? 'ms' : ''}
                </React.Fragment>
            )
        },
        {
            Header: 'Enabled',
            accessor: 'disabled',
            disableSortBy: true,
            Cell: ({ cell }) => (
                <React.Fragment>
                    <div className='form-check form-switch'>
                        <input checked={!cell.row.values.disabled}
                               type="checkbox"
                               id="keepdata"
                               className="form-check-input"
                               disabled={true}
                               data-lpignore={true}
                        />
                    </div>
                </React.Fragment>
            )
        },
        ...(props.isAdmin ? [{
            Header: 'Actions',
            accessor: 'actions',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }: any) => (
                <React.Fragment>
                    <Link to={`/admin/alert-rules/edit/${cell.row.values.id}`} role="button"
                          className="btn btn-sm btn-primary">
                        <i className="fal fa-edit fa-fw"/><span>Edit</span>
                    </Link>
                    <Link to={`/admin/alert-rules/delete/${cell.row.values.id}`} role="button"
                          className="btn btn-sm btn-danger">
                        <i className="fal fa-trash-alt fa-fw"/><span>Delete</span>
                    </Link>
                </React.Fragment>
            )
        }] : [])
    ], [props.isAdmin]);

    // CSV Data transformation function
    const transformedData = useMemo(() => (csvData: any[]) => {
        return csvData.map((item: any) => {
            // Match by name since CSV data may be sorted differently than data array
            const originalRow = data.find(row => row.name === item.name);
            if (!originalRow) return item;

            return {
                ...item,
                entity: originalRow.entity
                    ? originalRow.entity
                    : originalRow.entitytype === 'host'
                        ? 'All Hosts'
                        : 'All Instances',
                entitytype: `${originalRow.entitytype === 'datasource' ? 'instance' : originalRow.entitytype} ${originalRow.metrictype}`,
                statistic: stringFormatting(originalRow.statistic),
                threshold: `${originalRow.decreasing ? '<=' : '>='} ${originalRow.threshold}${originalRow.units === '%' ? '%' : originalRow.units === 'ms' ? 'ms' : ''}`,
                disabled: !originalRow.disabled ? 'Yes' : 'No',
            };
        });
    }, [data]);

    const tableInstance = useTable({
            columns,
            data,
            initialState: {
                hiddenColumns: ['id'],
                pageIndex: initialPageIndex,
                pageSize: initialPageSize,
                sortBy: [{ id: 'name', desc: false }]
            }
        },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    return (
        <div className="col h-100">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-exclamation-triangle fa-fw mr-10" aria-hidden="true"/>
                    Alert Rules
                    <span className="badge bg-info" data-tip="Total alert rules count">{data.length}</span>
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapseAlerts" aria-expanded="false" aria-controls="collapseAlerts"/>
                </div>
                <div id="collapseAlerts" className="card-body collapse show">
                    {props.loading === DATA_EMPTY ? (
                        <div className="w-100 text-center text-muted my-3">
                            <i className="fal fa-ban fa-fw fa-2x"/>
                            <p>
                                <button type="button" className="btn btn-link" onClick={refresh}>No Alert Rules Found -
                                    Click to Refresh
                                </button>
                            </p>
                        </div>
                    ) : (
                        props.loading === DATA_FAILED ? (
                            <div className="w-100 text-center text-muted my-3">
                                <i className="fal fa-ban fa-fw fa-2x text-danger"/>
                                <p>
                                    <button type="button" className="btn btn-link" onClick={refresh}>Failed to Retrieve
                                        Alert Rules - Click to Refresh
                                    </button>
                                </p>
                            </div>
                        ) : (
                            <React.Fragment>
                                <div className="row row-cols-1 row-cols-md-2 table-search">
<div className="col col-md-10">
                            <ConditionalRender if={props.isAdmin}>
                                            <Link to="/admin/alert-rules/create" role="button"
                                                  className="btn btn-sm btn-success">
                                                <i className="fal fa-plus-square fa-fw"/><span>Create</span>
                                            </Link>
                                        </ConditionalRender>
                                        <TableCSVExport
                                            tableInstance={tableInstance}
                                            filename="Alert Rules"
                                            transformData={transformedData}
                                        />
                                    </div>
                                    <div className="col col-md-2">
                                        <SearchInput
                                            value={tableFilter}
                                            onChange={(e) => setTableFilter(e.target.value)}
                                            onClear={clearTableFilter}
                                            autoFocus
                                        />
                                    </div>
                                </div>
                                <ReactTable
                                    getTableProps={getTableProps}
                                    getTableBodyProps={getTableBodyProps}
                                    headerGroups={headerGroups}
                                    prepareRow={prepareRow}
                                    page={page}
                                    canPreviousPage={canPreviousPage}
                                    canNextPage={canNextPage}
                                    pageOptions={pageOptions}
                                    pageCount={pageCount}
                                    gotoPage={gotoPage}
                                    nextPage={nextPage}
                                    previousPage={previousPage}
                                    setPageSize={setPageSize}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    dataLength={data.length}
                                    error={null}
                                    loading={props.loading === DATA_LOADING}
                                />
                            </React.Fragment>
                        )
                    )}
                </div>
            </div>
        </div>
    )
}

List.propTypes = {
    refresh: PropTypes.func.isRequired,
};

export default List;
